import React, { useCallback } from 'react';
import { Button, Col, Modal, Row } from 'antd';
import { Input } from 'formik-antd';
import cx from 'classnames';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import { isNil } from 'lodash';
import { FormikProps } from 'formik';

import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import { getFormItemName } from 'utils/fieldArrayUtils';
import Radio from 'components/formik/Radio';
import { PayCodeInfoFormValues } from '../../FinancialInfo';
import { PayCodeInfoFormScheduleValues } from 'components/modals/FinancialInfoScheduleModal';
import { PayCodeModel, PayRateTypeModel } from 'api/backofficeApiClient';
import { ptoMultiRateId } from 'constants/MultiRateConstants';
import { FinancialFormValues } from 'hooks/useFinancialInfoTabs';
import { FinancialInfoCalculator } from 'components/modals/AssignmentDataModal/FinancialInfo/calculationUtils';

import style from '../../../AssignmentInfo/AssignmentInfo.module.less';

type Props = {
  payCodeIndex: number;
  formNamePrefix: string;
  payCodeForm?: PayCodeInfoFormValues;
  scheduledPayCodeInfo?: PayCodeInfoFormScheduleValues;
  payCodeDescription?: string;
  isScheduleMode: boolean;
  isCanUpdateScheduler?: boolean;
  isAdmin?: boolean;
  currentRateTypeInfo?: PayRateTypeModel;
  financialInfoForm: FormikProps<FinancialFormValues>;
  onPayCodeFieldsClear: (
    payRateFormItemName: string,
    payCodeId?: string,
  ) => void;
  checkIsPayRateDisable: (
    scheduledPayCodeInfo?: PayCodeInfoFormScheduleValues,
  ) => boolean | undefined;
  checkIsBillRateDisable: (
    scheduledPayCodeInfo?: PayCodeInfoFormScheduleValues,
  ) => boolean | undefined;
  getPayCodeItemById: (payCodeId?: string) => PayCodeModel | undefined;
  payRateFinancialInfoValue?: number | null;
};

export default function PayCodeDetails(props: Props) {
  const {
    payCodeIndex,
    formNamePrefix,
    payCodeForm,
    scheduledPayCodeInfo,
    payCodeDescription,
    isScheduleMode,
    isCanUpdateScheduler,
    isAdmin,
    currentRateTypeInfo,
    financialInfoForm,
    onPayCodeFieldsClear,
    checkIsPayRateDisable,
    checkIsBillRateDisable,
    getPayCodeItemById,
    payRateFinancialInfoValue,
  } = props;
  const isDisabledFields = isScheduleMode && !isCanUpdateScheduler;
  const currentRateTypeId = currentRateTypeInfo?.id;
  const isPTOPayType = currentRateTypeId === ptoMultiRateId;

  const areAllPayCodeFieldsEmpty = (
    payCodeForm: PayCodeInfoFormValues | undefined,
  ): boolean => {
    if (!payCodeForm) return true;

    const isDefaultMemberBilledIndicator =
      getPayCodeItemById(payCodeForm.payCodeId)
        ?.memberBilledIndicatorDefaultValue ===
      payCodeForm.memberBilledIndicator;

    const isPayRateEmpty =
      payRateFinancialInfoValue === payCodeForm.payRate ||
      isNil(payCodeForm.payRate) ||
      (payCodeForm as any).payRate === '';
    const isBillRateEmpty =
      isNil(payCodeForm.billRate) || (payCodeForm as any).billRate === '';
    const isSpecialInstructionsEmpty =
      isNil(payCodeForm.specialInstructions) ||
      (payCodeForm as any).specialInstructions === '';

    return (
      isPayRateEmpty &&
      isBillRateEmpty &&
      isSpecialInstructionsEmpty &&
      isDefaultMemberBilledIndicator
    );
  };

  const checkBillRatePTORestriction = useCallback(
    (isPaidByMember?: boolean) =>
      !isAdmin && (!isNil(isPaidByMember) ? isPaidByMember : true),
    [isAdmin],
  );

  const payCodeFieldName = `${formNamePrefix}[${payCodeIndex}]`;

  const payRateFormItemName = getFormItemName({
    name: 'payRate',
    index: payCodeIndex,
    prefix: formNamePrefix,
  });

  const billRateFormItemName = getFormItemName({
    name: 'billRate',
    index: payCodeIndex,
    prefix: formNamePrefix,
  });

  const specialInstructionsFormItemName = getFormItemName({
    name: 'specialInstructions',
    index: payCodeIndex,
    prefix: formNamePrefix,
  });

  const memberBilledIndicatorFormItemName = getFormItemName({
    name: 'memberBilledIndicator',
    index: payCodeIndex,
    prefix: formNamePrefix,
  });

  const hasPayRateCustomLogicFormItemName = getFormItemName({
    name: 'hasPayRateCustomLogic',
    index: payCodeIndex,
    prefix: formNamePrefix,
  });

  const hasBillRateCustomLogicFormItemName = getFormItemName({
    name: 'hasBillRateCustomLogic',
    index: payCodeIndex,
    prefix: formNamePrefix,
  });

  const isPayRateDisable = checkIsPayRateDisable(scheduledPayCodeInfo);

  const isBillRateDisable = checkIsBillRateDisable(scheduledPayCodeInfo);

  const checkIsPayRateDisabled = useCallback(() => {
    if (isDisabledFields || isPayRateDisable) return true;

    if (isPTOPayType) {
      return !isAdmin;
    }
  }, [isAdmin, isPayRateDisable, isDisabledFields, isPTOPayType]);

  const checkIsBillRateDisabled = useCallback(() => {
    if (isPTOPayType) {
      const isPaidByMember = payCodeForm?.memberBilledIndicator;

      return (
        checkBillRatePTORestriction(isPaidByMember) ||
        isDisabledFields ||
        isBillRateDisable
      );
    }

    return isDisabledFields || isBillRateDisable;
  }, [
    isPTOPayType,
    isBillRateDisable,
    isDisabledFields,
    payCodeForm,
    checkBillRatePTORestriction,
  ]);

  const handlePaidByChange = useCallback(
    (event: RadioChangeEvent) => {
      if (isPTOPayType && !payCodeForm?.hasBillRateCustomLogic) {
        const mbpPortionValue = FinancialInfoCalculator.getMbpPortionValue(
          (financialInfoForm as any).values?.payRate,
          (financialInfoForm as any).values?.mbpRate,
        );

        const ptoBillRate = FinancialInfoCalculator.calculateBillRatePTOValue(
          (financialInfoForm as any).values?.billRate,
          mbpPortionValue,
          event.target.value,
        );

        financialInfoForm?.setFieldValue(
          billRateFormItemName,
          ptoBillRate,
          false,
        );
        financialInfoForm?.setFieldTouched(billRateFormItemName, true, false);
      }
    },
    [
      isPTOPayType,
      billRateFormItemName,
      financialInfoForm,
      payCodeForm?.hasBillRateCustomLogic,
    ],
  );

  const handlePayRateChanged = useCallback(() => {
    if (!isPTOPayType) return null;

    if (payCodeForm?.hasPayRateCustomLogic) return null;

    financialInfoForm?.setFieldValue(
      hasPayRateCustomLogicFormItemName,
      true,
      false,
    );
  }, [
    financialInfoForm,
    hasPayRateCustomLogicFormItemName,
    payCodeForm?.hasPayRateCustomLogic,
    isPTOPayType,
  ]);

  const handleBillRateChanged = useCallback(() => {
    if (!isPTOPayType) return null;

    if (payCodeForm?.hasBillRateCustomLogic) return null;

    financialInfoForm?.setFieldValue(
      hasBillRateCustomLogicFormItemName,
      true,
      false,
    );
  }, [
    financialInfoForm,
    hasBillRateCustomLogicFormItemName,
    payCodeForm?.hasBillRateCustomLogic,
    isPTOPayType,
  ]);

  const handlePayCodeFieldsClear = () =>
    Modal.confirm({
      title: 'Are you sure you want to remove the pay code data?',
      icon: <span className="icon icon-attention-filled" />,
      onOk() {
        onPayCodeFieldsClear(payCodeFieldName, payCodeForm?.payCodeId);
      },
      onCancel() {},
    });

  return (
    <Row gutter={40}>
      {!isNil(payCodeDescription) && (
        <Col xs={24} md={24}>
          <div className={style.info}>
            <i className="icon icon-info-filled" />
            {payCodeDescription}
          </div>
        </Col>
      )}
      <Col xs={24} md={12}>
        <Row gutter={40}>
          {payCodeForm?.hasOwnProperty('payRate') && (
            <Col xs={24} md={24}>
              <FormItem name={payRateFormItemName} label="Pay rate *">
                <InputNumber
                  name={payRateFormItemName}
                  placeholder="0.00"
                  className="full-width"
                  precision={2}
                  inputPrefix={'$'}
                  disabled={checkIsPayRateDisabled()}
                  onChange={() => handlePayRateChanged()}
                />
              </FormItem>
            </Col>
          )}
          {payCodeForm?.hasOwnProperty('billRate') && (
            <Col xs={24} md={24}>
              <FormItem name={billRateFormItemName} label="Bill rate *">
                <InputNumber
                  name={billRateFormItemName}
                  placeholder="0.00"
                  className="full-width"
                  precision={2}
                  inputPrefix={'$'}
                  disabled={checkIsBillRateDisabled()}
                  onChange={() => handleBillRateChanged()}
                />
              </FormItem>
            </Col>
          )}
          {!isScheduleMode &&
            payCodeForm?.hasOwnProperty('memberBilledIndicator') && (
              <Col xs={24} md={24}>
                <FormItem
                  className={cx('inline-radio', style.radio, style.test)}
                  name={memberBilledIndicatorFormItemName}
                >
                  <div>Paid by</div>
                  <Radio.Group
                    className="radio"
                    name={memberBilledIndicatorFormItemName}
                    options={[
                      { label: 'Member', value: true },
                      { label: 'Customer', value: false },
                    ]}
                    disabled={isDisabledFields}
                    onChange={handlePaidByChange}
                  />
                </FormItem>
              </Col>
            )}
        </Row>
      </Col>
      {payCodeForm?.hasOwnProperty('specialInstructions') && (
        <Col xs={24} md={12}>
          <FormItem
            name={specialInstructionsFormItemName}
            label="Special instructions"
            className={style.assignmentArea}
          >
            <Input.TextArea
              name={specialInstructionsFormItemName}
              placeholder="Enter special instructions"
              disabled={isDisabledFields}
            />
          </FormItem>
        </Col>
      )}
      {!isScheduleMode && (
        <Col xs={24}>
          <div className={style.btnWrapper}>
            <Button
              onClick={() => handlePayCodeFieldsClear()}
              icon={<i className="icon icon-close-sm" />}
              className="with-icon"
              disabled={
                areAllPayCodeFieldsEmpty(payCodeForm) ||
                !isNil(scheduledPayCodeInfo)
              }
            >
              Clear all fields
            </Button>
          </div>
        </Col>
      )}
    </Row>
  );
}
